import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  GlobalStyles,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Papa from 'papaparse';
import { useRef } from 'react';
import React from 'react';
import * as XLSX from 'xlsx';

import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import { Unit } from '@/Units/types';

import { ISubscibersBody } from '../types';

const useStyles = makeStyles({
  paper: {
    minHeight: 560,
    borderRadius: 10,
  },
});

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: Function;
  loading?: boolean;
  units: Unit[];
};

export const FormBulkSubscriber = ({
  open,
  onClose,
  onSubmit,
  loading: _loading = false,
  units,
}: Props) => {
  const classes = useStyles();
  const [isWrongData, setIsWrongData] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [fileType, setFileType] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
  // A parsed file whose rows put the same email in several units waits here for
  // explicit confirmation instead of uploading silently; `summary` lists each
  // multi-unit tenant so the user can spot an accidental duplicate before it
  // becomes a second tenancy.
  const [pendingUpload, setPendingUpload] = React.useState<{
    data: ISubscibersBody;
    summary: string[];
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Uploads a fully validated (and possibly user-confirmed) payload
  const submitUpload = (data: ISubscibersBody) => {
    setIsLoading(true);
    setPendingUpload(null);
    onSubmit(data, fileType)
      .then(() => {
        setIsLoading(false);
        onClose();
      })
      .catch((error: any) => {
        setIsLoading(false);
        resetFileInput();
        // Handle API errors
        if (error?.response?.data?.message) {
          setValidationErrors([`API Error: ${error.response.data.message}`]);
        } else if (error?.message) {
          setValidationErrors([`Error: ${error.message}`]);
        } else {
          setValidationErrors(['An unexpected error occurred while uploading']);
        }
        setIsWrongData(true);
      });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const file = event.target.files?.[0];
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension === 'csv') {
        setFileType('CSV');
        handleCsvFile(file);
      } else if (extension === 'xlsx') {
        setFileType('XLSX');
        handleXlsxFile(file);
      } else {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();

        // Provide helpful error message for Numbers files
        if (extension === 'numbers') {
          setValidationErrors([
            'Mac Numbers files (.numbers) are not supported.',
            'Please export your Numbers file as CSV or Excel format:',
            '1. Open your file in Numbers',
            '2. Go to File → Export To → CSV (or Excel)',
            '3. Save the exported file',
            '4. Upload the CSV or Excel file here',
          ]);
        } else {
          setValidationErrors([
            `File type ".${extension}" is not supported.`,
            'Please upload a CSV (.csv) or Excel (.xlsx) file.',
          ]);
        }
      }
    }
  };

  const handleCsvFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        processData(results.data);
      },
      error: (_error) => {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();
      },
    });
  };

  const handleXlsxFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target) {
        const data = new Uint8Array(event.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (jsonData.length === 0) {
          console.error('The file is empty.');
          setIsLoading(false);
          resetFileInput();
          return;
        }

        const headers: string[] = jsonData[0] as string[];
        const parsedData = jsonData.slice(1).map((row: any[]) => {
          const rowData: any = {};
          headers.forEach((header: string, index: number) => {
            // Handle undefined/null values
            const cellValue = row[index];
            rowData[header] =
              cellValue === undefined || cellValue === null ? '' : String(cellValue);
          });
          return rowData;
        });

        processData(parsedData);
      } else {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleClose = () => {
    // Reset error state
    setIsWrongData(false);
    setValidationErrors([]);
    setPendingUpload(null);

    // Reset file type
    setFileType('');

    // Reset loading state
    setIsLoading(false);

    // Clear file input
    resetFileInput();

    // Call the original onClose prop
    onClose();
  };

  const processData = (jsonData: any[]) => {
    // Convert headers to lowercase and trim spaces
    const normalizeData = (data: any[]) => {
      return data.map((item) => {
        return Object.fromEntries(
          Object.entries(item).map(([key, value]) => [key.trim().toLowerCase(), value]),
        );
      });
    };

    // Normalize the incoming data
    jsonData = normalizeData(jsonData);

    // Filter data to remove any entry with an empty 'unit name'
    jsonData = jsonData.filter((item: any) => item['unit name']?.trim() !== '');

    if (jsonData.length === 0) {
      setIsWrongData(true);
      setIsLoading(false);
      resetFileInput();
      return;
    }

    // Phone number normalization and validation function
    const normalizeAndValidatePhoneNumber = (
      phoneNumber?: string | null,
    ): { isValid: boolean; normalized: string } => {
      if (!phoneNumber || phoneNumber.trim() === '') {
        return { isValid: true, normalized: '' }; // Optional field
      }

      // Clean up the phone number - remove all non-digit characters except +
      let cleaned = phoneNumber.replace(/[^\d+]/g, '');

      // If it doesn't start with +, assume it's a US number and add +1
      if (!cleaned.startsWith('+')) {
        // Extract only digits
        const digitsOnly = cleaned.replace(/\D/g, '');

        // If it's 10 digits, assume US number
        if (digitsOnly.length === 10) {
          cleaned = '+1' + digitsOnly;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
          // If it's 11 digits starting with 1, assume US number with country code
          cleaned = '+' + digitsOnly;
        } else {
          // For other lengths, we can't assume the country code
          return { isValid: false, normalized: cleaned };
        }
      }

      // Find country code
      const countryCodes = [
        '+1',
        '+44',
        '+61',
        '+49',
        '+33',
        '+81',
        '+82',
        '+86',
        '+91',
        '+52',
        '+55',
        '+34',
        '+39',
        '+31',
        '+46',
        '+47',
        '+45',
        '+358',
        '+41',
      ];
      const countryCode = countryCodes
        .sort((a, b) => b.length - a.length)
        .find((code) => cleaned.startsWith(code));

      if (!countryCode) {
        return { isValid: false, normalized: cleaned };
      }

      const localNumber = cleaned.slice(countryCode.length);
      const numbersOnly = localNumber.replace(/\D/g, '');

      // US/Canada numbers should be 10 digits
      if (countryCode === '+1') {
        const isValid = numbersOnly.length === 10;
        return { isValid, normalized: cleaned };
      }

      // International numbers should be 6-15 digits
      const isValid = numbersOnly.length >= 6 && numbersOnly.length <= 15;
      return { isValid, normalized: cleaned };
    };

    // Comprehensive validation that collects all errors
    const errors: string[] = [];
    // The same email may appear on several rows (one tenant in several units);
    // only the same (email, unit) pair twice is a duplicate. Rows sharing an
    // email describe one person, so their names/phones must agree.
    const emailUnitPairs = new Set<string>();
    const personFieldsByEmail = new Map<string, { [field: string]: string }>();
    const requiredHeaders = ['unit name', 'email address', 'first name', 'last name'];
    const personFieldHeaders = ['first name', 'last name', 'phone number'];

    // Validate each row
    jsonData.forEach((item, index) => {
      const rowNumber = index + 2; // +2 because index is 0-based and we skip header row

      // Check required headers
      requiredHeaders.forEach((header) => {
        if (!(header in item) || !item[header] || item[header].trim() === '') {
          errors.push(`Row ${rowNumber}: Missing required field "${header}"`);
        }
      });

      // Check email format, duplicate (email, unit) pairs, and person-field consistency
      const email = item['email address'];
      if (email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          errors.push(`Row ${rowNumber}: Invalid email format "${email}"`);
        } else {
          const normalizedEmail = email.trim().toLowerCase();
          const pairKey = `${normalizedEmail}::${(item['unit name'] || '').trim()}`;
          if (emailUnitPairs.has(pairKey)) {
            errors.push(
              `Row ${rowNumber}: "${email}" is listed more than once for unit "${item['unit name']}"`,
            );
          } else {
            emailUnitPairs.add(pairKey);
          }

          const recorded = personFieldsByEmail.get(normalizedEmail) || {};
          personFieldHeaders.forEach((field) => {
            let value = (item[field] || '').trim();
            if (!value) return;
            // Compare phones in normalized form so "(555) 123-4567" and
            // "555-123-4567" are recognized as the same number
            if (field === 'phone number') {
              value = normalizeAndValidatePhoneNumber(value).normalized || value;
            }
            if (recorded[field] !== undefined && recorded[field] !== value) {
              errors.push(
                `Row ${rowNumber}: Rows for "${email}" disagree on ${field} ("${recorded[field]}" vs "${value}"). Rows with the same email describe one tenant and must match.`,
              );
            }
            recorded[field] = value;
          });
          personFieldsByEmail.set(normalizedEmail, recorded);
        }
      }

      // Check phone number
      const phoneNumber = item['phone number'];
      if (phoneNumber && phoneNumber.trim() !== '') {
        const phoneResult = normalizeAndValidatePhoneNumber(phoneNumber);
        if (!phoneResult.isValid) {
          errors.push(`Row ${rowNumber}: Invalid phone number "${phoneNumber}"`);
        }
      }

      // Check unit name exists
      const unitName = item['unit name'];
      if (unitName) {
        const unit = units.find((u) => u.name === unitName);
        if (!unit) {
          errors.push(`Row ${rowNumber}: Unit "${unitName}" not found in your account`);
        }
      }
    });

    if (errors.length === 0) {
      const rowEntries = jsonData.map((item: any) => {
        const unit = units.find((u) => u.name === item['unit name']);
        // Normalize and validate phone number
        const phoneResult = normalizeAndValidatePhoneNumber(item['phone number']);

        // Extract digits from normalized phone number for API
        let phoneDigits = '';
        if (phoneResult.normalized && phoneResult.isValid) {
          // Always send the full normalized phone number with country code
          phoneDigits = phoneResult.normalized;
        }

        return {
          email: item['email address'],
          first_name: item['first name'],
          last_name: item['last name'],
          phone_number: phoneDigits || undefined,
          unit_subscriptions: unit ? [{ unit_id: unit.id }] : [],
          unit_name: unit ? unit.name : '',
        };
      });

      const hasInvalidUnit = rowEntries.some((entry) => entry.unit_subscriptions.length === 0);

      if (hasInvalidUnit) {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();
      } else {
        setIsWrongData(false);

        // Merge rows sharing an email into ONE subscriber with all their units;
        // validation above already guaranteed the person fields agree
        const merged = new Map<
          string,
          { entry: (typeof rowEntries)[number]; unitNames: string[] }
        >();
        rowEntries.forEach((entry) => {
          const key = entry.email.trim().toLowerCase();
          const existing = merged.get(key);
          if (!existing) {
            merged.set(key, { entry: { ...entry }, unitNames: [entry.unit_name] });
          } else {
            existing.entry.unit_subscriptions = [
              ...existing.entry.unit_subscriptions,
              ...entry.unit_subscriptions,
            ];
            existing.entry.phone_number = existing.entry.phone_number || entry.phone_number;
            existing.unitNames.push(entry.unit_name);
          }
        });

        const subscribers = Array.from(merged.values()).map(({ entry }) => ({
          email: entry.email,
          first_name: entry.first_name,
          last_name: entry.last_name,
          phone_number: entry.phone_number,
          unit_subscriptions: entry.unit_subscriptions,
        }));
        const data: ISubscibersBody = { subscribers };

        // One tenant on several rows is legal but worth a look before upload:
        // pause for explicit confirmation with the multi-unit tenants listed
        const multiUnitSummary = Array.from(merged.values())
          .filter(({ unitNames }) => unitNames.length > 1)
          .map(
            ({ entry, unitNames }) =>
              `${`${entry.first_name ?? ''} ${entry.last_name ?? ''}`.trim() || entry.email} (${
                entry.email
              }) will be a tenant in ${unitNames.length} units: ${unitNames.join(', ')}`,
          );
        if (multiUnitSummary.length > 0) {
          setPendingUpload({ data, summary: multiUnitSummary });
          setIsLoading(false);
          return;
        }

        submitUpload(data);
      }
    } else {
      setValidationErrors(errors);
      setIsWrongData(true);
      setIsLoading(false);
      resetFileInput();
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '.MuiOutlinedInput-root': {
            borderRadius: '14px !important',
            minHeight: '45px !important',
            padding: '0 !important',
          },
          '.MuiOutlinedInput-input': {
            padding: '8px 12px !important',
            lineHeight: '1.5em',
          },
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth={'sm'}
        fullWidth
        classes={{
          paper: classes.paper,
        }}
      >
        <DialogTitle
          id="form-dialog-title"
          sx={{ margin: 'auto', textAlign: 'center', paddingTop: 5 }}
        >
          <Stack direction={'column'}>
            <Box sx={{ fontSize: '42px', fontWeight: 'bold' }}>
              {isWrongData
                ? 'Error importing tenants'
                : pendingUpload
                ? 'Confirm your import'
                : 'Import new tenants'}
            </Box>
            <Box sx={{ fontSize: '18px' }}>
              {isWrongData
                ? 'We tried uploading your file, but received errors.'
                : pendingUpload
                ? 'Some tenants in this file appear in more than one unit'
                : 'Import a list of new tenants and assign them to a property'}
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }} />
        <DialogContent sx={{ margin: 3 }}>
          {!isWrongData && !pendingUpload && <RenderInfo />}
          {isWrongData && <RenderWarningInfo validationErrors={validationErrors} />}
          {!isWrongData && pendingUpload && (
            <Stack spacing={2}>
              <Alert severity="info" sx={{ textAlign: 'left' }}>
                Rows sharing an email address are the same tenant. If any line below is a mistake,
                cancel and correct the spreadsheet; nothing has been imported yet.
              </Alert>
              {pendingUpload.summary.map((line) => (
                <Typography key={line} fontSize="15px">
                  {line}
                </Typography>
              ))}
              <Typography fontSize="13px" color="text.secondary">
                Importing never removes existing tenants or sets move-out dates; it only adds new
                tenants and updates the ones listed in the file.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />
        <DialogActions sx={{ justifyContent: 'space-between !important', margin: 2 }}>
          <SWLoadingButtonOutlined onClick={handleClose}>Cancel</SWLoadingButtonOutlined>
          {pendingUpload ? (
            <LoadingButton
              loading={isLoading}
              variant="contained"
              color="primary"
              onClick={() => submitUpload(pendingUpload.data)}
            >
              Confirm Import
            </LoadingButton>
          ) : (
            <>
              <LoadingButton
                loading={isLoading}
                variant="contained"
                color="primary"
                onClick={handleButtonClick}
              >
                Upload Spreadsheet
              </LoadingButton>
              <input
                aria-label="upload spreadsheet"
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

const RenderInfo = () => {
  const handleTemplateDownload = (e: React.MouseEvent) => {
    e.preventDefault();

    // Fetch the CSV file and force download
    fetch('/tenants_template.csv')
      .then((response) => response.blob())
      .then((blob) => {
        // Create blob URL
        const url = window.URL.createObjectURL(blob);

        // Create temporary anchor element and trigger download
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tenants_template.csv';
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error('Failed to download template:', err);
        // Fallback to direct navigation if blob download fails
        window.location.href = '/tenants_template.csv';
      });
  };

  return (
    <Stack spacing={3}>
      <Box>
        <span style={{ fontWeight: 'bold' }}>
          1.{' '}
          <a
            href="/tenants_template.csv"
            download="tenants_template.csv"
            onClick={handleTemplateDownload}
          >
            Download this template.
          </a>
        </span>{' '}
        This pre-formatted spreadsheet is designed to ensure your information is accurately captured
        and processed efficiently.
      </Box>
      <Box>
        <span style={{ fontWeight: 'bold' }}>2. Add your tenant's information.</span> Please fill
        out the spreadsheet with your data. Required fields are marked with an asterisk (*).
        <Box paddingLeft={2} paddingTop={1}>
          <ul style={{ margin: 0 }}>
            <li>Unit Name *</li>
            <li>Email Address *</li>
            <li>Phone Number (optional)</li>
            <li>First Name *</li>
            <li>Last Name *</li>
          </ul>
        </Box>
      </Box>
      <Box>
        <span style={{ fontWeight: 'bold' }}>3. Save and Upload.</span> Once you have filled out the
        spreadsheet with your information, save the file as CSV or XLSX format.
        <Box paddingLeft={2} paddingTop={1} sx={{ fontSize: '0.9em', color: 'text.secondary' }}>
          <strong>Mac Numbers users:</strong> Go to File → Export To → CSV or Excel to save in a
          compatible format.
        </Box>
        Return to this page and click on the "Upload Spreadsheet" button. Select your file and
        submit it for processing.
      </Box>
    </Stack>
  );
};

const RenderWarningInfo = ({ validationErrors }: { validationErrors: string[] }) => (
  <Stack spacing={3}>
    {validationErrors.length > 0 ? (
      <Box>
        <Box sx={{ fontWeight: 'bold', marginBottom: 1 }}>
          Found {validationErrors.length} error{validationErrors.length > 1 ? 's' : ''} in your
          file:
        </Box>
        <Box
          sx={{
            maxHeight: 300,
            overflowY: 'auto',
            backgroundColor: '#f5f5f5',
            padding: 2,
            borderRadius: 1,
            border: '1px solid #ddd',
          }}
        >
          {validationErrors.map((error, index) => (
            <Box key={index} sx={{ marginBottom: 0.5, fontSize: '14px', color: '#d32f2f' }}>
              • {error}
            </Box>
          ))}
        </Box>
      </Box>
    ) : (
      <Box>
        <Box>Please check the data in your spreadsheet. Here's a few common issues...</Box>
        <Box paddingLeft={2} paddingTop={1}>
          <ol style={{ margin: 0 }}>
            <li>
              Make sure your unit name is in the spreadsheet match your unit names in the web
              application
            </li>
            <li>Double check there are valid email addresses in the email address field</li>
            <li>
              Ensure phone numbers are valid (accepts formats like +1234567890, 1234567890, (123)
              456-7890, 123 456 7890)
            </li>
            <li>Make sure the file is a CSV or XLSX.</li>
          </ol>
        </Box>
      </Box>
    )}
    <Box>Still need help? Contact support@simplesubwater.com</Box>
  </Stack>
);

export default FormBulkSubscriber;
